import { createContext, useContext, useMemo, type PropsWithChildren } from 'react'
import { useMeetingStore } from '@stores/meeting'
import { useGetAppointment } from '@graphql/meeting/useAppointment'
import type { Appointment } from '@graphql/meeting/meeting.types'

type Interlocutor = {
  firstName: string
  phone?: string | null
}

type MeetingDataContextValue = {
  interlocutorName: string | null
  interlocutor: Interlocutor | null
  currentUserName: string | null
  isLoading: boolean
  appointment: Appointment | null
}

const MeetingDataContext = createContext<MeetingDataContextValue | undefined>(undefined)

export const MeetingDataProvider = ({ children }: PropsWithChildren) => {
  const appointmentId = useMeetingStore((s) => s.appointmentId)
  const { appointment, loading } = useGetAppointment(appointmentId ?? '')

  const value = useMemo(
    () => ({
      interlocutorName: appointment?.interlocutor?.firstName ?? null,
      interlocutor: appointment?.interlocutor
        ? {
            firstName: appointment.interlocutor.firstName,
            phone: appointment.interlocutor.phone
          }
        : null,
      currentUserName: appointment?.me?.firstName ?? null,
      isLoading: loading,
      appointment: appointment ?? null
    }),
    [appointment, loading]
  )

  return <MeetingDataContext.Provider value={value}>{children}</MeetingDataContext.Provider>
}

export const useMeetingData = () => {
  const context = useContext(MeetingDataContext)
  if (!context) {
    throw new Error('useMeetingData must be used within MeetingDataProvider')
  }
  return context
}
